import { CheckIcon, FlagIcon, PassIcon, QuestionnaireIcon, VerifiedTickIcon } from "@xola/icons";
import { Badge, Dot, Tooltip } from "@xola/ui-kit";
import {
    DemographicIcon,
    getQuestionnaireStatusIconColor,
    handleSuccessfulSubmission,
    isOtherSellerPurchase,
    PurchaseActionOption,
    PurchaseItemStatus,
} from "common";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    CheckInValidation,
    CheckInValidationCount,
    CheckInValidationType,
    CouponCode,
    DigitalWaiver,
    Experience,
    ExperiencePurchaseItem,
    Form,
    Guest,
    Permission,
    Product,
    Purchase,
    PurchaseItem,
} from "types";
import { RefundOption } from "../../Dashboard/RefundOption";
import { LinkedWaiverParticipantPopover } from "../../PurchaseModification/Waiver/LinkedWaiverParticipantPopover";
import { QuestionnaireModal } from "../../Questionnaire/QuestionnaireModal";
import { SplitPayGuestMenu } from "./SplitPayGuestMenu";
import { api } from "@/api/api";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import { ExpandableList } from "@/components/Common/ExpandableList";
import { IsGranted } from "@/components/Common/IsGranted";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { CollectPurchaseBalanceModal } from "@/components/PurchaseModification/CollectPurchaseBalanceModal";
import { PriceStrategy } from "@/components/PurchaseModification/PaymentBreakdown";
import { PurchaseReturnPaymentModal } from "@/components/PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { usePurchaseFormConfig } from "@/hooks/questionnaireFormHooks";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import { isDefaultDemographic } from "@/models/DemographicUnit";
import { getGuestsToSave, hasAddonFieldsInGuest, hasIdVerifiedCheckIn, isPrimaryGuest } from "@/models/Guest";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { getSeller } from "@/utils/getSeller";

interface PurchaseAttendeesProps {
    purchaseItem: PurchaseItem;
    form?: Form;
    purchase: Purchase;
    onOptionsChange: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
    ) => void;
}

const expandableListStyles = {
    container: {
        className: "relative flex flex-col space-y-2 divide-y divide-gray-light border-b border-gray-light pb-2",
    },
    button: { className: "my-0 mx-[40%] w-[20%]" },
};

export const PurchaseAttendees = ({ purchaseItem, form, purchase, onOptionsChange }: PurchaseAttendeesProps) => {
    const invalidate = useInvalidate();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const collectBalanceModal = useModal();
    const refundModal = useModal();

    const [purchaseItemId, setPurchaseItemId] = useState(purchaseItem.id);
    const [modifiedPurchase, setModifiedPurchase] = useState<Purchase>();
    const [refundAmount, setRefundAmount] = useState<number>(0);

    const {
        mutateAsync: updatePurchase,
        isLoading,
        reset,
    } = useMutation(async (payload: any) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
    });

    const handleQuestionnaireSubmit = async (updatedGuest: Guest, retainPriceStrategy?: PriceStrategy) => {
        const guests = getGuestsToSave(updatedGuest, purchaseItem.guests, ["waiver", "checkInCount", "ticketCode"]);
        const item = {
            id: purchaseItem.id,
            guests,
            ...(retainPriceStrategy && { _priceStrategy: retainPriceStrategy }),
        };
        setIsSubmitting(true);
        try {
            const { data: modifiedPurchase } = await updatePurchase({ items: [item] });
            setModifiedPurchase(modifiedPurchase);

            await handleSuccessfulSubmission(() => {
                reset();
                if (hasAddonFieldsInGuest(updatedGuest)) {
                    if (purchaseItem?.status === PurchaseItemStatus.CONFIRMED && modifiedPurchase.balance > 0) {
                        collectBalanceModal.show();
                    } else if (modifiedPurchase.balance < 0) {
                        setRefundAmount(Math.abs(modifiedPurchase.balance));
                        refundModal.show();
                    }
                } else {
                    invalidatePurchase();
                }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const invalidatePurchase = () => {
        invalidate("purchases", purchase.id);
    };

    const handleCollectBalanceModalClose = () => {
        invalidatePurchase();
        collectBalanceModal.hide();
    };

    const getFilteredGuests = (isRemoveGuestOpen: boolean, guest: Guest) => {
        return isRemoveGuestOpen
            ? purchaseItem.guests.map((guestItr) => {
                  if (guestItr.id === guest?.id) {
                      return { ...guestItr, _remove: true };
                  }

                  return guestItr;
              })
            : [guest];
    };

    useEffect(() => {
        setPurchaseItemId(purchaseItem.id);
    }, [purchaseItem]);

    return (
        <>
            <ExpandableList
                hideOnChange={purchaseItemId !== purchaseItem.id}
                styles={expandableListStyles}
                sortField="sequence"
                entities={purchaseItem.guests}
                component={(guest) => (
                    <PurchaseAttendee
                        key={guest.id}
                        guest={guest}
                        form={form}
                        purchase={purchase}
                        isSaving={isLoading || isSubmitting}
                        purchaseItem={purchaseItem}
                        onOptionsChange={onOptionsChange}
                        onQuestionnaireSubmit={handleQuestionnaireSubmit}
                        onFilterGuests={getFilteredGuests}
                    />
                )}
            />
            {modifiedPurchase && collectBalanceModal.isVisible && (
                <CollectPurchaseBalanceModal
                    isOpen={collectBalanceModal.isVisible}
                    purchase={modifiedPurchase}
                    onClose={handleCollectBalanceModalClose}
                />
            )}
            {modifiedPurchase && refundModal.isVisible && (
                <PurchaseReturnPaymentModal
                    isOpen={refundModal.isVisible}
                    purchase={modifiedPurchase}
                    refundAmount={refundAmount}
                    shouldReduce={false}
                    onClose={() => {
                        refundModal.hide();
                        invalidatePurchase();
                    }}
                />
            )}
        </>
    );
};

interface PurchaseAttendeeProps {
    purchase: Purchase;
    purchaseItem: PurchaseItem;
    guest: Guest;
    form?: Form;
    isSaving: boolean;
    onOptionsChange: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
    ) => void;
    onQuestionnaireSubmit: (guest: Guest) => Promise<void>;
    onFilterGuests: (isRemoveGuestOpen: boolean, guest: Guest) => Guest[];
}

const PurchaseAttendee = ({
    purchase,
    purchaseItem,
    guest,
    form,
    onOptionsChange,
    onQuestionnaireSubmit,
    onFilterGuests,
    isSaving,
}: PurchaseAttendeeProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isGranted } = useAuthorization();

    const { data: productItem } = useResource<Product>(["products", purchaseItem?.product.id]);
    const { data: waiverTemplates } = useCollection<DigitalWaiver>("waiverTemplates", null, {
        enabled: isGranted(Permission.WAIVERS_VIEW) && !isOtherSellerPurchase(purchase, getSeller().id),
    });

    const guestUserId = guest.formResponse?.user?.id;
    const fetchParams = {
        enabled:
            !!guestUserId &&
            isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW) &&
            !isOtherSellerPurchase(purchase, getSeller().id),
    };
    const { traveler } = useTraveler(guestUserId, null, fetchParams);
    const isPrimary = isPrimaryGuest(guest, undefined, purchaseItem);
    const isCheckInVerified = hasIdVerifiedCheckIn(guest);
    const isMembershipGuest = !!guest.membershipCode;
    const canEditPurchase = !isOtherSellerPurchase(purchase, getSeller().id) || purchase.partner?.editPurchases;

    const isWaiverLinked = productItem ? isProductHasLinkedWaiverTemplate(waiverTemplates, productItem) : false;

    const { hasGuestConfigOrResponse } = usePurchaseFormConfig(purchaseItem);

    const demographic = useMemo(() => {
        const catalogItem = productItem?.catalog?.items?.find(
            (item) => guest?.unit?.id && item.unit?.id === guest.unit.id,
        );
        const templateId = catalogItem?.template?.id ?? guest?.template?.id;

        return purchaseItem.lineItems.find((lineItem) => lineItem.template?.id === templateId);
    }, [purchaseItem, guest, productItem]);

    return (
        <div
            key={guest.id}
            className="flex w-full items-center justify-between gap-4 pt-1"
            data-testid="purchase-attendees-item"
        >
            <div className="flex w-0 flex-grow flex-col">
                <div className="flex flex-grow gap-1">
                    <NoTranslate as="div" className="min-w-0">
                        <AutoTooltipContainer tooltipContent={<NoTranslate>{guest.name}</NoTranslate>}>
                            <div className="truncate">
                                <NoTranslate>{guest.name}</NoTranslate>
                            </div>
                        </AutoTooltipContainer>
                    </NoTranslate>
                    <div className="mx-1 flex min-w-0 items-center gap-x-1">
                        {isPrimary && (
                            <Tooltip content="Main Attendee">
                                <FlagIcon />
                            </Tooltip>
                        )}

                        {isCheckInVerified && (
                            <Tooltip content="Identity Verified">
                                <VerifiedTickIcon size="tiny" />
                            </Tooltip>
                        )}
                    </div>
                </div>
                {isMembershipGuest ? (
                    <div className="mt-0.5">
                        <Badge color="secondary" className="uppercase" icon={<PassIcon />}>
                            {guest.membershipCode ? (guest.membershipCode as CouponCode).code : "NA"}
                        </Badge>
                    </div>
                ) : (
                    <AutoTooltipContainer
                        tooltipContent={
                            <NoTranslate if={!isDefaultDemographic(demographic?.name)}>{demographic?.name}</NoTranslate>
                        }
                    >
                        <div className="truncate">
                            <DemographicIcon name={demographic?.name ?? ""} className="mr-1 flex-shrink-0" />
                            <NoTranslate className="text-sm" if={!isDefaultDemographic(demographic?.name)}>
                                {demographic?.name}
                            </NoTranslate>
                        </div>
                    </AutoTooltipContainer>
                )}
            </div>
            <IsGranted permissions={Permission.MODIFY_GUEST_VIEW}>
                <div className="flex flex-nowrap space-x-1">
                    {!isMembershipGuest && <CheckInCount guest={guest} purchaseItem={purchaseItem} />}

                    {form && hasGuestConfigOrResponse && canEditPurchase ? (
                        <span>
                            <Tooltip content="Questionnaire">
                                <SecondaryButton
                                    size="tiny"
                                    data-testid="purchase-attendees-questionnaire-button"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <Dot
                                        className="mr-1"
                                        color={getQuestionnaireStatusIconColor(form, guest, isPrimary)}
                                    />
                                    <QuestionnaireIcon />
                                </SecondaryButton>
                            </Tooltip>
                        </span>
                    ) : null}

                    {!isMembershipGuest && canEditPurchase && (
                        <>
                            {isWaiverLinked && !isOtherSellerPurchase(purchase, getSeller().id) && (
                                <LinkedWaiverParticipantPopover guest={guest} purchaseItem={purchaseItem} />
                            )}
                            <SplitPayGuestMenu
                                guest={guest}
                                purchase={purchase}
                                purchaseItem={purchaseItem as ExperiencePurchaseItem}
                                traveler={traveler}
                                onOptionsChange={onOptionsChange}
                                onFilterGuests={onFilterGuests}
                            />
                        </>
                    )}
                    {form ? (
                        <QuestionnaireModal
                            disableRequiredFields
                            isOpen={isModalOpen}
                            form={form}
                            guest={guest}
                            purchase={purchase}
                            isSaving={isSaving}
                            onClose={() => setIsModalOpen(false)}
                            onSubmit={onQuestionnaireSubmit}
                        />
                    ) : null}
                </div>
            </IsGranted>
        </div>
    );
};

interface CheckInCountProps {
    guest: Guest;
    purchaseItem: PurchaseItem;
}

const CheckInCount = ({ purchaseItem, guest }: CheckInCountProps) => {
    const seller = useSeller();
    const { data: experience } = useResource<Experience>(["experiences", purchaseItem.product.id]);

    if (!experience) {
        return null;
    }

    const checkInPreference = experience.checkInPreference ?? seller.preferences.checkIn;
    const isCheckInEnabled = checkInPreference?.enabled;
    const { multiple, max } = parseValidations(checkInPreference?.validations ?? []);
    if (!isCheckInEnabled || !multiple) {
        return null;
    }

    return (
        <span className="mr-3">
            <CheckIcon className="mr-1" />
            <span data-testid="guest-checkin-count">
                {guest.checkInCount}
                {max ? `/${max}` : null}
            </span>
        </span>
    );
};

export const parseValidations = (validations: CheckInValidation[]) => {
    const countValidation = validations.find((validation) => validation.object === CheckInValidationType.COUNT) as
        | CheckInValidationCount
        | undefined;

    const hasMultipleCheckIn = !countValidation || countValidation.max > 1;

    return {
        multiple: hasMultipleCheckIn,
        max: countValidation ? countValidation.max : undefined,
    };
};
